import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Center,
  Loader,
  Text,
  Stack,
  Title,
  Paper,
  Badge,
  Group,
  Button,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconFileText,
  IconDownload,
} from "@tabler/icons-react";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../../utils/downloadFilename";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/**
 * Public portal report viewer.
 * Accessible without authentication via a token link
 * emailed by the 'Portal Access' delivery method.
 *
 * Renders the report PDF inline with a download option.
 */
export default function PortalReportView() {
  const { token } = useParams<{ token: string }>();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadFileName, setDownloadFileName] = useState("report.pdf");
  const [pdfBlob, setPdfBlob] = useState<Blob>();

  useEffect(() => {
    if (!token) {
      setError("Invalid portal link — no token provided.");
      setLoading(false);
      return;
    }

    axios
      .get(`${API_BASE_URL}/report/portal/${token}/pdf`, {
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        setPdfBlob(blob);
        setPdfUrl(URL.createObjectURL(blob));
        setDownloadFileName(
          getResponseDownloadFilename(
            res.headers["content-disposition"],
            "report.pdf"
          )
        );
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.status === 403
            ? "This report link has been revoked. Please contact the laboratory for assistance."
            : err.response?.status === 404
            ? "Report not found or link is invalid."
            : "Unable to load the report. The link may have expired or is invalid.";
        setError(message);
        setLoading(false);
      });

    return () => {
      // Revoke object URL on unmount
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [token]);

  const handleDownload = useCallback(() => {
    if (pdfBlob) triggerBlobDownload(pdfBlob, downloadFileName);
  }, [downloadFileName, pdfBlob]);

  if (loading) {
    return (
      <Center style={{ minHeight: "100vh", backgroundColor: "#f4f6f9" }}>
        <Stack align="center" gap="md">
          <Loader size="lg" color="blue" />
          <Text c="dimmed">Loading report…</Text>
        </Stack>
      </Center>
    );
  }

  if (error || !pdfUrl) {
    return (
      <Center style={{ minHeight: "100vh", backgroundColor: "#f4f6f9" }}>
        <Paper
          shadow="sm"
          p="xl"
          radius="md"
          style={{ maxWidth: 480, width: "100%" }}
        >
          <Stack align="center" gap="md">
            <IconAlertCircle size={48} color="var(--mantine-color-red-6)" />
            <Title order={3} c="red.7">
              Report Unavailable
            </Title>
            <Text ta="center" c="dimmed" size="sm">
              {error ||
                "This report link is invalid or has been revoked. Please contact the laboratory for assistance."}
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  return (
    <Box
      style={{
        backgroundColor: "#f4f6f9",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header bar */}
      <Box
        style={{
          backgroundColor: "#1a3c5e",
          padding: "12px 24px",
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconFileText size={24} color="white" />
            <Text c="white" fw={700} size="lg">
              Secure Report Viewer
            </Text>
          </Group>
          <Group gap="sm">
            <Badge color="green" variant="light" size="sm">
              Secure Link
            </Badge>
            <Button
              size="xs"
              variant="white"
              color="dark"
              leftSection={<IconDownload size={14} />}
              onClick={handleDownload}
            >
              Download PDF
            </Button>
          </Group>
        </Group>
      </Box>

      {/* PDF viewer */}
      <Box style={{ flex: 1, padding: "16px" }}>
        <iframe
          src={`${pdfUrl}#toolbar=0&navpanes=0`}
          title="Report PDF"
          style={{
            width: "100%",
            height: "calc(100vh - 72px)",
            border: "none",
            borderRadius: 8,
          }}
        />
      </Box>
    </Box>
  );
}
